import * as React from 'react';
import { Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MAX_PERSONAL_WORDS, personalWordKey, type PersonalDictionary } from './personal-dictionary';

export const PERSONAL_DICTIONARY_PAGE_SIZE = 25;

/** Review, search, add and remove the words spell check accepts for this person. */
export function PersonalDictionaryDialog({ open, onOpenChange, dictionary }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dictionary: PersonalDictionary;
}) {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [draft, setDraft] = React.useState('');
  const [problem, setProblem] = React.useState('');
  const inputId = React.useId();
  const searchId = React.useId();

  React.useEffect(() => { if (!open) { setSearch(''); setPage(0); setDraft(''); setProblem(''); } }, [open]);

  const query = personalWordKey(search);
  const filtered = React.useMemo(() => query ? dictionary.words.filter(word => word.includes(query)) : dictionary.words, [dictionary.words, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PERSONAL_DICTIONARY_PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PERSONAL_DICTIONARY_PAGE_SIZE, (current + 1) * PERSONAL_DICTIONARY_PAGE_SIZE);

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    const result = dictionary.add(draft);
    if (!result.ok) { setProblem(result.message); return; }
    setProblem(''); setDraft('');
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Personal dictionary</DialogTitle>
        <DialogDescription>Words here are never marked as misspelt. They are saved in this browser for you, up to {MAX_PERSONAL_WORDS} words.</DialogDescription>
      </DialogHeader>
      <form className="space-y-2" onSubmit={add}>
        <Label htmlFor={inputId}>Add a word</Label>
        <div className="flex gap-2">
          <Input id={inputId} value={draft} maxLength={64} autoComplete="off" aria-invalid={problem ? true : undefined} aria-describedby={problem ? inputId + '-problem' : undefined}
            onChange={event => { setDraft(event.target.value); setProblem(''); }} />
          <Button type="submit" disabled={!draft.trim()}>Add</Button>
        </div>
        {problem && <p id={inputId + '-problem'} role="alert" className="text-sm text-destructive">{problem}</p>}
      </form>
      <div className="space-y-2">
        <Label htmlFor={searchId}>Search your words</Label>
        <Input id={searchId} type="search" value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} />
      </div>
      {filtered.length === 0
        ? <p className="py-6 text-center text-sm text-muted-foreground">{dictionary.words.length ? 'No words match your search.' : 'Your dictionary is empty. Right-click an underlined word and choose Add to dictionary, or add a word above.'}</p>
        : <ul aria-label="Dictionary words" className="max-h-72 divide-y overflow-y-auto rounded-md border">
          {visible.map(word => <li key={word} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
            <span className="min-w-0 break-words">{word}</span>
            <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${word}`} onClick={() => dictionary.remove(word)}><Trash2Icon aria-hidden /></Button>
          </li>)}
        </ul>}
      <DialogFooter className="items-center sm:justify-between">
        <span className="text-xs text-muted-foreground" aria-live="polite">{filtered.length} {filtered.length === 1 ? 'word' : 'words'}{pages > 1 ? ` · page ${current + 1} of ${pages}` : ''}</span>
        {pages > 1 && <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</Button>
          <Button type="button" variant="outline" size="sm" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>Next</Button>
        </div>}
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
